/**
 * Example media with a guaranteed-stable box.
 *
 * The aspect ratio is reserved up front and a deterministic gradient paints immediately, so
 * the grid never reflows when a photo arrives (or fails). Real images fade in over that
 * gradient — which doubles as the blur-up placeholder and as the permanent artwork for the
 * many ingested prompts that have no example image at all.
 */

import { useState } from 'react';
import type { ExampleMedia } from '@promptvault/shared';
import { artBackground, formatDuration } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { Icon } from './Icon';

export interface MediaProps {
  /** Seed for the fallback art — always the prompt id, so art is stable per prompt. */
  seed: string;
  example?: ExampleMedia | null;
  /** CSS aspect-ratio, e.g. '4 / 5'. */
  ratio?: string;
  alt?: string;
  variant?: number;
  className?: string;
  /** Blur the media until tapped (NSFW, when the user has enabled NSFW at all). */
  blurred?: boolean;
  onReveal?: () => void;
  children?: React.ReactNode;
  /** Above-the-fold media loads eagerly; everything else waits for the scroll. */
  priority?: boolean;
}

export function Media({
  seed,
  example,
  ratio = '4 / 5',
  alt = '',
  variant = 0,
  className = '',
  blurred = false,
  onReveal,
  children,
  priority = false,
}: MediaProps) {
  const { t, lang } = useI18n();
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  /**
   * Video, which this component simply did not handle.
   *
   * It resolved `thumbUrl || url` into an `<img>`. For the 397 video prompts there was no thumb,
   * so the src was an `.mp4`, which fails to decode — `onError` fired, `failed` went true, and the
   * card fell back to the gradient. Every video in the product rendered as abstract art except
   * inside the Lightbox, which is the only place with a `<video>` branch.
   */
  /**
   * The **media's** own type, which is the ground truth about what this box contains.
   *
   * `PromptCard` used to decide the same question from `card.type === 'video'` and draw a second
   * badge and a second pill of its own on top of these. One tile, two play glyphs, two durations —
   * and the two answers could disagree, because a prompt's declared type and the type of the file
   * we actually hold are different facts. The chrome that sits on media belongs to the component
   * that draws the media, so there is one place for it to be right or wrong.
   */
  const isVideo = example?.mediaType === 'video';
  const duration = formatDuration(example?.duration ?? null, lang);
  const poster = example?.thumbUrl || null;
  const url = poster || example?.url || null;

  /**
   * Poster here; playback in the Lightbox.
   *
   * Not "autoplay whatever is in view". A rail is a horizontal list of eight or more cells, and a
   * `<video>` per cell is a range request per cell — on a phone, inside Telegram, which is where
   * this product's users are. A 47 KB poster is one small image; the 6.8 MB video is fetched only
   * when someone opens the item, and `Lightbox.tsx` already has the `<video>` with controls.
   *
   * So this renders the poster and a play badge that is deliberately *not* interactive: the tap
   * belongs to the card, which opens the item. Two tap targets stacked on one tile is how you get
   * a card that sometimes navigates and sometimes does not. See DECISIONS.md D39.
   */
  const showImage = Boolean(url) && !failed && !(isVideo && !poster);

  return (
    <div
      className={`relative isolate overflow-hidden bg-media-placeholder ${className}`}
      style={{ aspectRatio: ratio }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: artBackground(seed, variant) }}
      />
      {showImage && (
        <img
          src={url!}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-fast ease-pv"
          style={{ opacity: loaded ? 1 : 0 }}
        />
      )}

      {/**
       * The play affordance, and the duration.
       *
       * Shown whenever this is a video and it is not currently playing — including when the
       * poster is missing, because a gradient with a play badge still reads as "this is a video"
       * where a bare gradient reads as "this is broken".
       *
       * **Both are hidden while blurred.** A blurred NSFW tile that still advertises "play" is
       * describing the thing it is withholding, which is the opposite of what the blur is for.
       *
       * Sized as a fraction of the box rather than a fixed 44px, because `Media` is also the
       * 44px thumbnail on a leaderboard row and a collection cover — at that size a fixed badge
       * fills the entire tile.
       */}
      {isVideo && !blurred && (
        <>
          <span
            role="img"
            aria-label={t.playVideo}
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <span
              className="flex items-center justify-center rounded-full text-on-media backdrop-blur-sm"
              style={{
                width: 'min(32%, 44px)',
                aspectRatio: '1',
                background: 'var(--pv-media-scrim)',
              }}
            >
              <Icon name="play" className="h-1/2 w-1/2" />
            </span>
          </span>
          {/*
            `dir="ltr"` and `.tnum`: "1:05" is a clock reading, not a sentence, and it must not
            be reordered by the RTL flow. `formatDuration` localises the digits, so a Farsi card
            reads ۱:۰۵ — the previous local `clock()` here emitted ASCII while the card's own
            copy of this pill emitted Persian, on the same tile.

            `text-caption2`, not `text-2xs`: the latter is defined in the **admin's** Tailwind
            config and in no scale this app has, so it produced no CSS at all and the pill
            rendered at inherited body size.
          */}
          {duration && (
            <span
              dir="ltr"
              className="tnum pointer-events-none absolute bottom-1.5 end-1.5 rounded-full px-1.5 py-0.5 text-caption2 font-medium text-on-media"
              style={{ background: 'var(--pv-media-scrim-strong)' }}
            >
              {duration}
            </span>
          )}
        </>
      )}

      {/* Fine cross-hatch: the design's texture pass over every media tile. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 mix-blend-overlay"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, rgb(255 255 255 / 0.06) 0 1px, rgb(0 0 0 / 0.05) 1px 3px)',
        }}
      />
      {/**
       * Blurred, and honest about whether it can be un-blurred.
       *
       * This was one `<button>` reading "Tap to reveal", rendered whether or not a reveal
       * existed. `PromptCard` passes `blurred` with **no** `onReveal`, so on every grid card the
       * handler was `undefined`, the click fell through to the card's own `<button>` — which this
       * one was illegally nested inside — and the tile navigated instead of revealing. The
       * affordance named the one thing it could not do.
       *
       * So the reveal is drawn only where a reveal is wired (the detail Gallery, which owns the
       * state). Everywhere else this is an inert label: the tile says what it is holding back and
       * the tap belongs to the card, exactly as it does on every other tile.
       *
       * The blur itself stays unconditional on `isNsfw` either way — D15's second gate, which is
       * deliberately independent of the viewer's `nsfwVisible` setting.
       */}
      {blurred &&
        (onReveal ? (
          <button
            type="button"
            /*
              The gallery frame calls `setPointerCapture` on pointerdown to drive its swipe, which
              redirects the rest of the sequence to the frame — so the `click` never reached this
              button and the reveal did nothing on any prompt with more than one example. The ⤢
              button forty lines below in `Gallery` has carried this exact guard since it was
              written; this one never got it, and no test pressed it until now.
            */
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onReveal}
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-on-media backdrop-blur-xl"
            style={{ background: 'var(--pv-media-scrim)' }}
          >
            <Icon name="eye" size={22} />
            <span className="text-caption font-medium">{t.revealNsfw}</span>
          </button>
        ) : (
          <span
            className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 text-on-media backdrop-blur-xl"
            style={{ background: 'var(--pv-media-scrim)' }}
          >
            <Icon name="eye" size={22} />
            <span className="text-caption font-medium">{t.nsfwBadge}</span>
          </span>
        ))}
      {children}
    </div>
  );
}
